/* eslint-disable max-len */
/* eslint-disable indent */
// Modules
import React, { useEffect, useMemo, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import { Loading, Radio } from '@dasa-health/components-react'

// Application
import { Contact, ContactType } from 'typings'

// Components
import { LoadingContainer, StyledForm } from './styles'
import { SignUpHelpTextModal } from 'redesign/components/atoms/SignUpHelpTextModal'
import { SignUpFooter } from 'redesign/components/molecules/SignUpFooter'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { UnicoSession } from 'redesign/components/atoms/UnicoSession'
import { clearCpf, getQueryParamsValue } from 'utils'
import { useRecoverHook } from 'hooks'
import { UNICO_CONSTANTS } from 'redesign/constants'
import { BiometyDataType } from 'typings/unico'
import { SignUpModalInfo } from './SignUpModalInfo'
import { SignUpModalDuvidas } from './SignUpModalDuvidas'
import { SignUpModalError } from './SignUpModalError'
import { ModalFeedbackUnico } from './ModalFeedbackUnico'
import { useBiometry } from 'hooks/useBiometry'
import { useLocation } from 'react-router-dom'
import { UnicoSessionDefault } from 'redesign/components/atoms/UnicoSessionDefault'

export interface SelectContactInitial {
    contact?: Contact
}
interface Props {
    initialValues: SelectContactInitial
    handleSubmit: (values: SelectContactInitial) => void
    contacts: Array<Contact>
    isSubmitting: boolean
    submitText?: string
    isLoading?: boolean
    isLogged?: boolean
}

export const SelectContact: React.FC<Props> = ({
    initialValues,
    contacts,
    handleSubmit,
    isSubmitting,
    submitText = 'Continuar',
    isLoading = false,
    isLogged = false,
}) => {
    const [showModal, setShowModal] = useState(false)
    const [showModalError, setShowModalError] = useState(false)
    const [showModalDuvidas, setShowModalDuvidas] = useState(false)
    const [showModalFeedbackUnico, setShowModalFeedbackUnico] = useState(false)
    const [formInitialValues, setFormInitialValues] = useState<SelectContactInitial>(initialValues)
    const [biometyData, setBiometyData] = useState<BiometyDataType>({
        federalTaxId: undefined,
        id: undefined,
        contacts: [],
    })
    const [biometryLimitExceeded, setBiometryLimitExceeded] = useState(false)

    const { isFeatureEnabled } = useRemoteConfig()
    const recover = useRecoverHook()
    const location = useLocation()
    const erro = getQueryParamsValue(location, 'erro')

    const { fluxoAtual, isLoadingBiometry, createBiometry, checkBiometryLimit } = useBiometry(contacts)

    const biometyDataLS: BiometyDataType = JSON.parse(sessionStorage.getItem(UNICO_CONSTANTS.STORAGE)!)
    const personalData = JSON.parse(sessionStorage.getItem('@LiviaAuth:SignUpPersonalData')!)
    const userCpf = clearCpf(recover?.username || personalData?.cpf || biometyDataLS?.federalTaxId || '')
    const hasCipPassword = biometyDataLS?.hasCipPassword

    const IS_UNICO_ENABLED = isFeatureEnabled('AUTH_ENABLE_UNICO')
    const showUnicoOption = IS_UNICO_ENABLED && !isLogged && userCpf
    const hideUnicoOption = !IS_UNICO_ENABLED

    async function preHandleSubmit(values: SelectContactInitial) {
        const isUnico = values.contact?.type === ContactType.UNICO

        if (!hasCipPassword && isUnico) {
            setShowModalFeedbackUnico(true)
            setBiometryLimitExceeded(true)
            return
        }
        const checkLimit = await checkBiometryLimit()

        if (isUnico && checkLimit === UNICO_CONSTANTS.CHECK_BIOMETRY_LIMIT.LIMIT_EXCEEDED) {
            setShowModalFeedbackUnico(true)
            setBiometryLimitExceeded(true)
            return
        }

        if (isUnico) {
            sessionStorage.setItem(
                UNICO_CONSTANTS.STORAGE,
                JSON.stringify({
                    ...biometyData,
                    unico: true,
                }),
            )
            setBiometyData({
                ...biometyData,
                federalTaxId: userCpf,
                contacts,
                origin: fluxoAtual,
                unico: true,
            })
            await createBiometry()

            return
        }

        handleSubmit(values)
    }

    const contactsToMap = contacts?.length ? contacts : biometyDataLS?.contacts || []

    const isLoadingGeneral = useMemo(() => isLoading || isLoadingBiometry, [isLoading, isLoadingBiometry])
    useEffect(() => {
        erro && setShowModalError(true)
    }, [erro])

    useEffect(() => {
        async function checkBiometryConditions() {
            if (!showUnicoOption) {
                setBiometryLimitExceeded(false)

                return
            }

            if (!hasCipPassword) {
                setBiometryLimitExceeded(true)
                return
            }

            const checkLimit = await checkBiometryLimit()
            if (checkLimit === UNICO_CONSTANTS.CHECK_BIOMETRY_LIMIT.LIMIT_EXCEEDED) {
                setBiometryLimitExceeded(true)
            }
        }

        checkBiometryConditions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            {isLoadingGeneral ? (
                <LoadingContainer>
                    <Loading />
                </LoadingContainer>
            ) : (
                <Formik
                    enableReinitialize={true}
                    initialValues={formInitialValues}
                    onSubmit={async (values, { setSubmitting }) => {
                        setFormInitialValues(values)
                        await preHandleSubmit(values)
                        setSubmitting(false)
                    }}
                >
                    {({ values, setFieldValue }) => (
                        <StyledForm>
                            <SignUpModalInfo setShowModal={setShowModal} showModal={showModal} />
                            <SignUpModalDuvidas setShowModal={setShowModalDuvidas} showModal={showModalDuvidas} />
                            <SignUpModalError setShowModal={setShowModalError} showModal={showModalError} />
                            <ModalFeedbackUnico
                                hasCipPassword={hasCipPassword}
                                setShowModal={setShowModalFeedbackUnico}
                                showModal={showModalFeedbackUnico}
                            />

                            <Form autoComplete="off" data-testid="select-contact-form">
                                {hideUnicoOption && (
                                    <SignUpHelpTextModal onClick={() => setShowModal(true)}>
                                        Não tem acesso a nenhum destes contatos?
                                    </SignUpHelpTextModal>
                                )}
                                <div className="form__radio-list">
                                    {contactsToMap.map((item: Contact) => (
                                        <div className="form__radio" key={item.id}>
                                            <Field
                                                as={Radio}
                                                checked={values.contact?.id === item.id}
                                                data-testid="radio-input"
                                                disabled={isSubmitting}
                                                label={item.value}
                                                name="contact"
                                                onChange={() => setFieldValue('contact', item)}
                                                value={item.id}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {showUnicoOption &&
                                    (biometryLimitExceeded ? (
                                        <UnicoSession isSubmitting={isSubmitting} preHandleSubmit={preHandleSubmit} />
                                    ) : (
                                        <UnicoSessionDefault setFieldValue={setFieldValue} />
                                    ))}

                                <SignUpFooter
                                    isSubmitButtonDisabled={!values.contact}
                                    isSubmitButtonLoading={isSubmitting}
                                    submitButtonText={IS_UNICO_ENABLED && !isLogged ? 'Validar conta' : submitText}
                                />
                            </Form>
                        </StyledForm>
                    )}
                </Formik>
            )}
        </>
    )
}
